import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import ModernGoogleAuth from '../components/ModernGoogleAuth';
import { auth, db } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { clearAllAuthCache, clearGoogleAuthCache, forceRefreshAuth, debugAuthCache } from '../utils/authCache';

const PartnerLoginPage = ({ darkMode }) => {
  const [formData, setFormData] = useState({
    partnerId: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentGoogleUser, setCurrentGoogleUser] = useState(null);
  const [partnerIdValidation, setPartnerIdValidation] = useState({
    isChecking: false,
    isValid: null,
    message: ''
  });
  const navigate = useNavigate();

  // Detect if user is already signed in with Google
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('Current Google user detected:', user.email);
        setCurrentGoogleUser(user);
      } else {
        console.log('No current Google user');
        setCurrentGoogleUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // API fallback for partner validation
  const validatePartnerIdViaAPI = async (partnerId) => {
    try {
      console.log('🔍 Validating partner ID via API:', partnerId);
      const response = await axios.get(`${API_BASE_URL}/api/admin/partners`);
      
      if (response.data.success && response.data.partners) {
        const isOVID = partnerId.startsWith('OV');
        let partnerExists = false;

        if (isOVID) {
          // Check if OV ID exists in API response
          partnerExists = response.data.partners.some(partner => 
            partner.partnerID === partnerId
          );
        } else {
          // Check if phone number exists in API response
          partnerExists = response.data.partners.some(partner => 
            partner.phoneNumber === partnerId || partner.id === partnerId
          );
        }

        if (partnerExists) {
          setPartnerIdValidation({ 
            isChecking: false, 
            isValid: true, 
            message: isOVID ? `✓ Partner ID ${partnerId} found` : `✓ Phone number ${partnerId} found`
          });
        } else {
          setPartnerIdValidation({ 
            isChecking: false, 
            isValid: false, 
            message: isOVID ? `✗ Partner ID ${partnerId} is not registered` : `✗ Phone number ${partnerId} is not registered`
          });
        }
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.error('API validation error:', error);
      setPartnerIdValidation({ 
        isChecking: false, 
        isValid: false, 
        message: 'Error checking registration' 
      });
    }
  };

  // Real-time validation for Partner ID
  const validatePartnerIdInRealTime = async (partnerId) => {
    if (!partnerId || partnerId.length < 3) {
      setPartnerIdValidation({ isChecking: false, isValid: null, message: '' });
      return;
    }

    setPartnerIdValidation({ isChecking: true, isValid: null, message: 'Checking...' });

    try {
      const isOVID = partnerId.startsWith('OV');
      
      if (isOVID) {
        // Check if OV ID format is valid
        if (!/^OV\d{1,2}$/.test(partnerId)) {
          setPartnerIdValidation({ 
            isChecking: false, 
            isValid: false, 
            message: 'Invalid OV ID format. Use format: OV01, OV02, etc.' 
          });
          return;
        }
      } else {
        // Check if phone number format is valid
        if (!/^\d{10}$/.test(partnerId)) {
          if (partnerId.length === 10) {
            setPartnerIdValidation({ 
              isChecking: false, 
              isValid: false, 
              message: 'Phone number must contain only digits' 
            });
          }
          return;
        }
      }

      let partnerExists = false;

      try {
        // Try Firebase first
        if (isOVID) {
          const partnersRef = collection(db, 'partners');
          const q = query(partnersRef, where('partnerID', '==', partnerId));
          const querySnapshot = await getDocs(q);
          partnerExists = !querySnapshot.empty;
        } else {
          // Check both document ID and phoneNumber field
          const partnerDocRef = doc(db, 'partners', partnerId);
          const docSnap = await getDoc(partnerDocRef);
          
          if (docSnap.exists()) {
            partnerExists = true;
          } else {
            // Also check by phoneNumber field
            const partnersRef = collection(db, 'partners');
            const q = query(partnersRef, where('phoneNumber', '==', partnerId));
            const querySnapshot = await getDocs(q);
            partnerExists = !querySnapshot.empty;
          }
        }

        if (partnerExists) {
          setPartnerIdValidation({ 
            isChecking: false, 
            isValid: true, 
            message: isOVID ? `✓ Partner ID ${partnerId} found` : `✓ Phone number ${partnerId} found`
          });
        } else {
          setPartnerIdValidation({ 
            isChecking: false, 
            isValid: false, 
            message: isOVID ? `✗ Partner ID ${partnerId} is not registered` : `✗ Phone number ${partnerId} is not registered`
          });
        }

      } catch (firebaseError) {
        console.log('🔒 Firebase permissions denied - using API fallback for validation');
        // Fallback to API validation
        await validatePartnerIdViaAPI(partnerId);
      }

    } catch (error) {
      console.error('Error validating partner ID:', error);
      setPartnerIdValidation({ 
        isChecking: false, 
        isValid: false, 
        message: 'Error checking registration' 
      });
    }
  };

  // Debounce function for real-time validation
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
  };

  // Debounced validation function
  const debouncedValidatePartnerId = debounce(validatePartnerIdInRealTime, 800);

  // API fallback for checking partner email
  const checkPartnerEmailViaAPI = async (email) => {
    try {
      console.log('🔍 Checking partner email via API:', email);
      const response = await axios.get(`${API_BASE_URL}/api/admin/partners`);
      
      if (response.data.success && response.data.partners) {
        const partner = response.data.partners.find(p => p.email === email);
        
        if (partner) {
          return {
            isAuthorized: true,
            partnerData: partner,
            partnerId: partner.id
          };
        }
      }
      
      return { isAuthorized: false };
    } catch (error) {
      console.error('Error checking partner email via API:', error);
      return { isAuthorized: false };
    }
  };

  // Check if email is registered as a partner
  const checkPartnerEmail = async (email) => {
    try {
      // Try Firebase first
      const partnersRef = collection(db, 'partners');
      const q = query(partnersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Found partner with this email
        const partnerDoc = querySnapshot.docs[0];
        return {
          isAuthorized: true,
          partnerData: partnerDoc.data(),
          partnerId: partnerDoc.id
        };
      }
      
      return { isAuthorized: false };
    } catch (error) {
      console.error('Firebase error, falling back to API:', error);
      // Fallback to API
      return await checkPartnerEmailViaAPI(email);
    }
  };

  const handleGoogleAuthSuccess = async (result) => {
    setGoogleLoading(true);
    try {
      const user = result.user;
      const email = user.email;
      
      console.log('Google auth successful, checking if email is authorized:', email);
      
      // Check if this email is registered as a partner
      const authCheck = await checkPartnerEmail(email);
      
      if (authCheck.isAuthorized) {
        // Store partner session
        sessionStorage.setItem('partnerSession', JSON.stringify({
          partnerId: authCheck.partnerId,
          partnerData: authCheck.partnerData,
          loginTime: new Date().toISOString(),
          loginMethod: 'google',
          userEmail: email
        }));

        console.log('Partner authorized via Google, redirecting to dashboard');
        navigate('/partner-dashboard');
      } else {
        setError('Access denied! This email is not registered as an authorized partner.');
        await auth.signOut(); // Sign out the unauthorized user
      }
    } catch (error) {
      console.error('Google auth success handler error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleAuthError = (error) => {
    setGoogleLoading(false);
    console.error('Google authentication error:', error);
    if (error.code === 'auth/popup-blocked') {
      setError('Popup was blocked. Please allow popups for this site and try again.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      setError('Authentication was cancelled. Please try again.');
    } else {
      setError('Google authentication failed. Please try again.');
    }
  };

  // Handle signing in with the already logged-in Google user
  const handleSignInAsCurrentUser = async () => {
    if (!currentGoogleUser) return;
    
    setGoogleLoading(true);
    try {
      const email = currentGoogleUser.email;
      console.log('Signing in with current Google user:', email);
      
      // Check if this email is registered as a partner
      const authCheck = await checkPartnerEmail(email);
      
      if (authCheck.isAuthorized) {
        // Store partner session
        sessionStorage.setItem('partnerSession', JSON.stringify({
          partnerId: authCheck.partnerId,
          partnerData: authCheck.partnerData,
          loginTime: new Date().toISOString(),
          loginMethod: 'google',
          userEmail: email
        }));

        console.log('Partner authorized via current Google user, redirecting to dashboard');
        navigate('/partner-dashboard');
      } else {
        setError('Access denied! This email is not registered as an authorized partner.');
      }
    } catch (error) {
      console.error('Current user auth error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // Get user's first name for display
  const getUserDisplayName = () => {
    if (!currentGoogleUser) return '';
    
    // Try to get first name from displayName
    if (currentGoogleUser.displayName) {
      const firstName = currentGoogleUser.displayName.split(' ')[0];
      return firstName;
    }
    
    // Fallback to email username
    if (currentGoogleUser.email) {
      const emailUsername = currentGoogleUser.email.split('@')[0];
      return emailUsername;
    }
    
    return 'User';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For partner ID, allow both OV ID format (OV01, OV02) and phone numbers (10 digits)
    if (name === 'partnerId') {
      const inputValue = value.trim();
      
      // If user starts typing "OV", convert to uppercase and allow alphanumeric
      if (inputValue.toUpperCase().startsWith('OV')) {
        const upperValue = inputValue.toUpperCase();
        // Allow OV followed by up to 2 digits, max length 4
        if (upperValue.length <= 4 && /^OV\d{0,2}$/.test(upperValue)) {
          setFormData({ ...formData, [name]: upperValue });
          // Trigger real-time validation
          debouncedValidatePartnerId(upperValue);
        }
      } else if (/^\d*$/.test(inputValue)) {
        // If input contains only digits, treat as phone number (max 10 digits)
        if (inputValue.length <= 10) {
          setFormData({ ...formData, [name]: inputValue });
          // Trigger real-time validation
          debouncedValidatePartnerId(inputValue);
        }
      } else {
        // Allow typing for OV ID - accept partial input like "O" or "OV"
        const upperValue = inputValue.toUpperCase();
        if (upperValue === 'O' || upperValue === 'OV' || upperValue.length <= 4) {
          setFormData({ ...formData, [name]: upperValue });
          // Reset validation for partial input
          if (upperValue.length < 3) {
            setPartnerIdValidation({ isChecking: false, isValid: null, message: '' });
          }
        }
      }
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const validatePartnerCredentials = (partnerId, password) => {
    // Determine if it's an OV ID or phone number
    const isOVID = partnerId.startsWith('OV');
    
    if (isOVID) {
      // Validate OV ID format (OV followed by 1-2 digits)
      if (!/^OV\d{1,2}$/.test(partnerId)) {
        return { isValid: false, message: 'Invalid OV ID format. Use format: OV01, OV02, etc.' };
      }
      
      // For OV IDs, validate that password ends with @OV2025 (flexible on prefix)
      if (!password.endsWith('@OV2025')) {
        return { isValid: false, message: 'Invalid password format' };
      }
    } else {
      // Validate phone number (10 digits)
      if (!/^\d{10}$/.test(partnerId)) {
        return { isValid: false, message: 'Phone number must be exactly 10 digits' };
      }
      
      // Validate password format for phone: {phone}@OV2025
      if (!password.endsWith('@OV2025')) {
        return { isValid: false, message: 'Invalid password format' };
      }
    }

    return { isValid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate credentials format first
      const validation = validatePartnerCredentials(formData.partnerId, formData.password);
      if (!validation.isValid) {
        setError(validation.message);
        setLoading(false);
        return;
      }

      // Determine login method and search for partner in Firebase
      const isOVID = formData.partnerId.startsWith('OV');
      let partnerDoc = null;
      let partnerData = null;
      let firebasePartnerId = null;

      console.log(`Attempting login with ${isOVID ? 'OV ID' : 'phone number'}:`, formData.partnerId);

      if (isOVID) {
        // Search by OV ID in partnerID field
        const partnersRef = collection(db, 'partners');
        const q = query(partnersRef, where('partnerID', '==', formData.partnerId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          partnerDoc = querySnapshot.docs[0];
          partnerData = partnerDoc.data();
          firebasePartnerId = partnerDoc.id;
          console.log('Partner found by OV ID:', partnerData);
        } else {
          console.log('No partner found with OV ID:', formData.partnerId);
        }
      } else {
        // Search by phone number (document ID is phone number OR search by phoneNumber field)
        try {
          // First try to find document with phone number as document ID
          const partnerDocRef = doc(db, 'partners', formData.partnerId);
          const docSnap = await getDoc(partnerDocRef);
          
          if (docSnap.exists()) {
            partnerData = docSnap.data();
            firebasePartnerId = formData.partnerId;
            console.log('Partner found by phone number (document ID):', partnerData);
          } else {
            // If not found by document ID, search by phoneNumber field
            console.log('Not found by document ID, searching by phoneNumber field...');
            const partnersRef = collection(db, 'partners');
            const q = query(partnersRef, where('phoneNumber', '==', formData.partnerId));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              partnerDoc = querySnapshot.docs[0];
              partnerData = partnerDoc.data();
              firebasePartnerId = partnerDoc.id;
              console.log('Partner found by phoneNumber field:', partnerData);
            } else {
              console.log('No partner found with phone number:', formData.partnerId);
            }
          }
        } catch (error) {
          console.error('Error fetching partner by phone:', error);
        }
      }

      // Check if partner was found
      if (!partnerData) {
        setError('Partner not found. Please check your Partner ID and try again.');
        setLoading(false);
        return;
      }

      // Verify password matches (check multiple password formats for flexibility)
      const isPasswordValid = 
        (partnerData.password === formData.password) || 
        (partnerData.phonePassword === formData.password) ||
        // For OV ID login, also allow OVID@OV2025 format even if stored password is different
        (isOVID && formData.password === `${formData.partnerId}@OV2025`) ||
        // For phone login, also allow phone@OV2025 format even if stored password is different
        (!isOVID && formData.password === `${formData.partnerId}@OV2025`);

      if (!isPasswordValid) {
        setError('Invalid password. Please check your credentials and try again.');
        setLoading(false);
        return;
      }

      // Store partner session with comprehensive data
      const sessionData = {
        partnerId: firebasePartnerId,
        partnerData: {
          ...partnerData,
          partnerId: firebasePartnerId
        },
        loginTime: new Date().toISOString(),
        loginMethod: 'credentials',
        loginType: isOVID ? 'ovid' : 'phone',
        userCredential: formData.partnerId
      };

      sessionStorage.setItem('partnerSession', JSON.stringify(sessionData));
      
      console.log('Partner login successful, session stored:', sessionData);

      // Navigate to partner dashboard
      navigate('/partner-dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const LoginIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
    </svg>
  );

  const PartnerIcon = () => (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  return (
    <div className={`min-h-screen relative overflow-hidden ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
    }`}>
      
      {/* Apple-style animated background */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        
        {/* Glassmorphism grid pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-3xl">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 0%, transparent 50%)`
          }}></div>
        </div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          
          {/* Main glassmorphism container with improved dark mode visibility */}
          <div className={`backdrop-blur-xl border rounded-3xl shadow-2xl p-8 relative overflow-hidden ${
            darkMode 
              ? 'bg-gray-900/80 border-gray-600/50' 
              : 'bg-white/10 border-white/20'
          }`}>
            
            {/* Subtle inner glow */}
            <div className={`absolute inset-0 rounded-3xl ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-700/10 via-transparent to-transparent' 
                : 'bg-gradient-to-br from-white/20 via-transparent to-transparent'
            }`}></div>
            
            {/* Content */}
            <div className="relative space-y-8">
              
              {/* Header with improved visibility */}
              <div className="text-center space-y-4">
                {/* Apple-style icon with better contrast */}
                <div className={`mx-auto w-20 h-20 rounded-2xl backdrop-blur-md border flex items-center justify-center mb-6 shadow-lg ${
                  darkMode 
                    ? 'bg-blue-600/30 border-blue-400/40 text-blue-300' 
                    : 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-white/30 text-blue-600'
                }`}>
                  <PartnerIcon />
                </div>
                
                <div className="space-y-2">
                  <h2 className={`text-3xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Partner Portal
                  </h2>
                  <p className={`text-lg font-medium ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                    Welcome, Vishwateja Partners
                  </p>
                  <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                    Login with Partner ID & Password or use Google Sign-in
                  </p>
                  <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    Use your OV ID (e.g., OV01) or phone number (10 digits)
                  </p>
                </div>
              </div>

              {/* Login Form with improved dark mode styling */}
              <form className="space-y-6" onSubmit={handleSubmit}>
                
                {error && (
                  <div className={`backdrop-blur-md border rounded-2xl p-4 ${
                    darkMode 
                      ? 'bg-red-900/40 border-red-700/50 text-red-200' 
                      : 'bg-red-500/20 border-red-500/30 text-red-800'
                  }`}>
                    <p className="text-sm text-center font-medium">{error}</p>
                    
                    {/* Troubleshooting Tools for Partner Login Issues */}
                    <div className="mt-4 pt-3 border-t border-red-300/30">
                      <p className="text-xs text-center mb-2">
                        🛠️ Having login or authentication issues? Try these troubleshooting tools:
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            clearGoogleAuthCache();
                            alert('Google auth cache cleared! Please try logging in again.');
                          }}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            darkMode
                              ? 'bg-red-700/30 hover:bg-red-700/40 text-red-200 border border-red-500/40'
                              : 'bg-red-600/20 hover:bg-red-600/30 text-red-900 border border-red-400/30'
                          }`}
                        >
                          Clear Google Cache
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            clearAllAuthCache();
                            alert('All auth cache cleared! Please refresh and try again.');
                          }}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            darkMode
                              ? 'bg-red-700/30 hover:bg-red-700/40 text-red-200 border border-red-500/40'
                              : 'bg-red-600/20 hover:bg-red-600/30 text-red-900 border border-red-400/30'
                          }`}
                        >
                          Clear All Cache
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            forceRefreshAuth();
                          }}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            darkMode
                              ? 'bg-red-800/40 hover:bg-red-800/50 text-red-100 border border-red-600/50'
                              : 'bg-red-700/30 hover:bg-red-700/40 text-red-900 border border-red-500/40'
                          }`}
                        >
                          Force Refresh
                        </button>
                      </div>
                      <div className="flex justify-center mt-2">
                        <button
                          type="button"
                          onClick={debugAuthCache}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            darkMode
                              ? 'bg-blue-700/30 hover:bg-blue-700/40 text-blue-200 border border-blue-500/40'
                              : 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-900 border border-blue-400/30'
                          }`}
                        >
                          🔍 Debug Auth (Check Console)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Partner ID Field with better contrast */}
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Partner ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="partnerId"
                      value={formData.partnerId}
                      onChange={handleChange}
                      placeholder="Enter phone number or OV ID"
                      className={`w-full p-4 rounded-2xl backdrop-blur-md border transition-all duration-300 ${
                        partnerIdValidation.isValid === true 
                          ? 'border-green-500/50 focus:border-green-500/70' 
                          : partnerIdValidation.isValid === false 
                          ? 'border-red-500/50 focus:border-red-500/70' 
                          : darkMode 
                          ? 'border-gray-600/50 focus:border-gray-500/70' 
                          : 'border-white/30 focus:border-white/50'
                      } ${
                        darkMode 
                          ? 'bg-gray-800/50 text-white placeholder-gray-300 focus:bg-gray-800/70' 
                          : 'bg-white/15 text-gray-900 placeholder-gray-500 focus:bg-white/20'
                      } focus:ring-2 focus:ring-blue-500/40 focus:outline-none shadow-lg`}
                      required
                    />
                    
                    {/* Validation Status Icon */}
                    {(partnerIdValidation.isChecking || partnerIdValidation.isValid !== null) && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {partnerIdValidation.isChecking ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                        ) : partnerIdValidation.isValid ? (
                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                          </svg>
                        )}
                      </div>
                    )}
                    
                    {/* Subtle inner shadow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                  
                  {/* Validation Message */}
                  {partnerIdValidation.message && (
                    <div className={`text-xs font-medium transition-all duration-300 ${
                      partnerIdValidation.isChecking 
                        ? 'text-blue-400' 
                        : partnerIdValidation.isValid 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      {partnerIdValidation.message}
                    </div>
                  )}
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className={`w-full p-4 pr-12 rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 transition-all duration-300 ${
                        darkMode 
                          ? 'text-white placeholder-gray-300' 
                          : 'text-gray-900 placeholder-gray-500'
                      } focus:bg-white/20 focus:border-white/40 focus:ring-2 focus:ring-blue-500/30 focus:outline-none shadow-lg`}
                      required
                    />
                    
                    {/* Password Toggle Button */}
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-lg transition-colors duration-200 ${
                        darkMode 
                          ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-black/5'
                      }`}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        // Eye slash icon (hide password)
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        // Eye icon (show password)
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                    
                    {/* Subtle inner shadow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-black/5 to-transparent pointer-events-none"></div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || (!formData.partnerId || formData.partnerId.length < 3)}
                  className={`group relative w-full py-4 px-6 rounded-2xl font-semibold text-white transition-all duration-300 transform ${
                    loading || (!formData.partnerId || formData.partnerId.length < 3)
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:scale-105 active:scale-95'
                  } bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-2xl hover:shadow-blue-500/25 overflow-hidden`}
                >
                  {/* Button background glow */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-2xl"></div>
                  
                  {/* Button content */}
                  <div className="relative flex items-center justify-center space-x-3">
                    {loading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <LoginIcon />
                        <span className="text-lg">Access Partner Dashboard</span>
                      </>
                    )}
                  </div>
                  
                  {/* Subtle shine effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 animate-pulse"></div>
                  </div>
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className={`w-full border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className={`px-2 ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500'}`}>
                      {currentGoogleUser ? `Sign in as ${getUserDisplayName()}?` : 'Or continue with'}
                    </span>
                  </div>
                </div>

                {/* Google Sign-in Button */}
                <div className="relative">
                  {googleLoading ? (
                    <div className="w-full py-4 px-6 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                      <Spinner size="sm" />
                      <span className="ml-2 text-white">Signing in with Google...</span>
                    </div>
                  ) : currentGoogleUser ? (
                    /* Show personalized button for current Google user */
                    <button
                      onClick={handleSignInAsCurrentUser}
                      className="group relative w-full flex justify-center items-center py-4 px-6 border border-transparent text-sm font-medium rounded-2xl text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl backdrop-blur-md"
                    >
                      <div className="flex items-center space-x-3">
                        {/* User avatar or Google icon */}
                        {currentGoogleUser.photoURL ? (
                          <img 
                            src={currentGoogleUser.photoURL} 
                            alt="User avatar" 
                            className="w-6 h-6 rounded-full border border-white/30"
                          />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        <span>Continue as {getUserDisplayName()}</span>
                      </div>
                    </button>
                  ) : (
                    /* Show modern Google sign-in for new users */
                    <div className="transform hover:scale-105 transition-all duration-300">
                      <ModernGoogleAuth
                        onSuccess={handleGoogleAuthSuccess}
                        onError={handleGoogleAuthError}
                        darkMode={darkMode}
                      />
                    </div>
                  )}
                  <p className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {currentGoogleUser 
                      ? `Signed in as ${currentGoogleUser.email}` 
                      : 'Use your registered email for quick access'
                    }
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* Troubleshooting Section for Authentication Issues */}
          <div className="mt-6">
            <details className={`backdrop-blur-xl border rounded-2xl p-4 shadow-lg ${
              darkMode 
                ? 'bg-gray-900/40 border-gray-600/50' 
                : 'bg-white/10 border-white/20'
            }`}>
              <summary className={`cursor-pointer text-sm font-medium transition-colors hover:text-blue-400 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                🛠️ Having login or authentication issues? Click here for troubleshooting tools
              </summary>
              <div className="mt-4 space-y-4">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  If you're experiencing problems with Google sign-in, partner login, or authentication errors, try these tools:
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      clearGoogleAuthCache();
                      alert('Google authentication cache cleared! Please try signing in again.');
                    }}
                    className={`px-3 py-2 text-xs rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
                      darkMode
                        ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                        : 'bg-white/20 hover:bg-white/30 text-gray-700 border border-gray-300/30'
                    }`}
                  >
                    Clear Google Cache
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      clearAllAuthCache();
                      alert('All authentication cache cleared! Please refresh the page and try again.');
                    }}
                    className={`px-3 py-2 text-xs rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
                      darkMode
                        ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                        : 'bg-white/20 hover:bg-white/30 text-gray-700 border border-gray-300/30'
                    }`}
                  >
                    Clear All Cache
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      forceRefreshAuth();
                    }}
                    className={`px-3 py-2 text-xs rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
                      darkMode
                        ? 'bg-red-600/20 hover:bg-red-600/30 text-red-200 border border-red-500/30'
                        : 'bg-red-500/20 hover:bg-red-500/30 text-red-700 border border-red-400/30'
                    }`}
                  >
                    Force Refresh
                  </button>
                </div>
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={debugAuthCache}
                    className={`px-3 py-2 text-xs rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
                      darkMode
                        ? 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-200 border border-blue-500/30'
                        : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-700 border border-blue-400/30'
                    }`}
                  >
                    🔍 Debug Auth State (Check Console)
                  </button>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                  These tools clear cached authentication data that may be causing issues with the new Google OAuth system.
                </p>
              </div>
            </details>
          </div>

          {/* Contact Information with better dark mode styling */}
          <div className="mt-8 text-center">
            <div className={`backdrop-blur-xl border rounded-2xl p-4 sm:p-6 space-y-3 shadow-lg ${
              darkMode 
                ? 'bg-gray-900/60 border-gray-600/50' 
                : 'bg-white/10 border-white/20'
            }`}>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-600'}`}>
                Need help? Contact Vishwateja Support
              </p>
              <div className="flex flex-col space-y-2">
                <a 
                  href="mailto:doctorworldconsultancy@gmail.com" 
                  className={`inline-flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 ${
                    darkMode 
                      ? 'text-blue-300 hover:text-blue-200 hover:bg-blue-500/10' 
                      : 'text-blue-600 hover:text-blue-700 hover:bg-blue-500/10'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-xs sm:text-sm break-all">doctorworldconsultancy@gmail.com</span>
                </a>
                <a 
                  href="https://wa.me/916364249519" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 ${
                    darkMode 
                      ? 'text-green-300 hover:text-green-200 hover:bg-green-500/10' 
                      : 'text-green-600 hover:text-green-700 hover:bg-green-500/10'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.687"/>
                  </svg>
                  <span className="font-medium text-xs sm:text-sm whitespace-nowrap">WhatsApp: +91 6364 249 519</span>
                </a>
              </div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-4`}>
                This portal is for authorized partners only
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default PartnerLoginPage;
